import { useEffect, useState } from "react";
import { http } from "@/lib/api";
import { useVenueMarket } from "@/hooks/useVenueMarket";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Trash2, Users, Layers, Ban, Loader2 } from "lucide-react";

const INTERVAL_LABEL = { monthly: "Monthly", six_monthly: "Every 6 months", annual: "Annual" };

function statusBadge(status) {
  const m = {
    active:    "bg-emerald-600",
    past_due:  "bg-amber-500",
    cancelled: "bg-zinc-500",
    expired:   "bg-zinc-400",
    pending:   "bg-zinc-300",
  }[status] || "bg-zinc-400";
  return <Badge className={`${m} text-white capitalize`}>{status?.replace("_", " ")}</Badge>;
}

export default function Members() {
  const [tab, setTab] = useState("tiers");
  const [tiers, setTiers] = useState(null);
  const [members, setMembers] = useState(null);
  const [editing, setEditing] = useState(null);
  const { fmt, symbol } = useVenueMarket();

  const loadTiers = async () => {
    const { data } = await http.get("/venues/me/memberships/tiers");
    setTiers(data);
  };
  const loadMembers = async () => {
    const { data } = await http.get("/venues/me/memberships");
    setMembers(data);
  };
  useEffect(() => { loadTiers(); loadMembers(); }, []);

  return (
    <div className="p-8 lg:p-10 max-w-6xl" data-testid="members-page">
      <div className="text-xs text-zinc-500 uppercase tracking-widest font-medium">Recurring revenue</div>
      <h1 className="mt-1 font-display font-black text-3xl lg:text-4xl tracking-tight">Members</h1>
      <p className="mt-2 text-sm text-zinc-600 max-w-lg">
        Turn one-off ticket buyers into a monthly base. Set custom membership tiers with your own labels and prices — Stripe handles the recurring billing.
      </p>

      {/* Tabs */}
      <div className="mt-8 border-b border-zinc-200 flex gap-1">
        {[
          { id: "tiers",   label: "Tiers",       icon: Layers },
          { id: "members", label: "Subscribers", icon: Users },
        ].map((t) => {
          const Icon = t.icon;
          const active = tab === t.id;
          return (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              data-testid={`members-tab-${t.id}`}
              className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px flex items-center gap-2 ${
                active ? "border-zinc-900 text-zinc-900" : "border-transparent text-zinc-500 hover:text-zinc-900"
              }`}
            >
              <Icon className="w-4 h-4" />{t.label}
            </button>
          );
        })}
      </div>

      {tab === "tiers" && (
        <TiersPanel
          tiers={tiers}
          fmt={fmt}
          onEdit={setEditing}
          onNew={() => setEditing({ mode: "new" })}
          onReload={loadTiers}
        />
      )}
      {tab === "members" && (
        <MembersPanel members={members} fmt={fmt} onReload={loadMembers} />
      )}

      {editing && (
        <TierEditor
          initial={editing.mode === "new" ? null : editing}
          symbol={symbol}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); loadTiers(); }}
        />
      )}
    </div>
  );
}

function TiersPanel({ tiers, fmt, onEdit, onNew, onReload }) {
  if (tiers === null) return <div className="mt-6 text-sm text-zinc-500">Loading…</div>;

  return (
    <div className="mt-6">
      <div className="flex justify-end mb-3">
        <Button onClick={onNew} className="rounded-full bg-zinc-900 hover:bg-zinc-800" data-testid="new-tier-btn">
          <Plus className="w-4 h-4 mr-2" /> New tier
        </Button>
      </div>
      {tiers.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-zinc-300 p-12 text-center">
          <Users className="w-8 h-8 mx-auto text-zinc-400" />
          <div className="mt-3 font-display font-bold text-lg">No membership tiers yet</div>
          <p className="mt-1 text-sm text-zinc-500 max-w-sm mx-auto">Create your first tier — could be a monthly patron, an annual friends-of-the-venue, or a supporter card.</p>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 gap-4">
          {tiers.map((t) => (
            <div
              key={t.id}
              data-testid={`tier-card-${t.id}`}
              className="rounded-2xl border border-zinc-200 bg-white p-6 hover:border-zinc-300 cursor-pointer transition"
              onClick={() => onEdit(t)}
            >
              <div className="flex items-center justify-between">
                <div className="font-display font-black text-xl">{t.label}</div>
                {t.status === "archived" && <Badge className="bg-zinc-500 text-white">Archived</Badge>}
              </div>
              <div className="mt-3 font-mono text-2xl">{fmt(t.price_pence)}</div>
              <div className="text-xs text-zinc-500 mt-0.5">{INTERVAL_LABEL[t.interval]}</div>
              {t.description && <p className="mt-3 text-sm text-zinc-600 line-clamp-2">{t.description}</p>}
              {t.perks?.length > 0 && (
                <ul className="mt-3 text-xs text-zinc-500 space-y-0.5">
                  {t.perks.slice(0, 3).map((p, i) => <li key={i}>· {p}</li>)}
                  {t.perks.length > 3 && <li>+ {t.perks.length - 3} more</li>}
                </ul>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function MembersPanel({ members, fmt, onReload }) {
  const [cancelling, setCancelling] = useState(null);

  const doCancel = async (m) => {
    if (!confirm(`Cancel ${m.member_email}'s ${m.tier_label} membership? Their subscription will end and they won't be charged again.`)) return;
    setCancelling(m.id);
    try {
      await http.post(`/venues/me/memberships/${m.id}/cancel`);
      toast.success("Membership cancelled");
      onReload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Cancel failed");
    } finally {
      setCancelling(null);
    }
  };

  if (members === null) return <div className="mt-6 text-sm text-zinc-500">Loading…</div>;
  if (members.length === 0) {
    return (
      <div className="mt-6 rounded-2xl border border-dashed border-zinc-300 p-12 text-center">
        <Users className="w-8 h-8 mx-auto text-zinc-400" />
        <div className="mt-3 font-display font-bold text-lg">No subscribers yet</div>
        <p className="mt-1 text-sm text-zinc-500 max-w-sm mx-auto">Once someone signs up for a membership tier, they&apos;ll appear here.</p>
      </div>
    );
  }

  return (
    <div className="mt-6 rounded-2xl border border-zinc-200 bg-white overflow-hidden" data-testid="members-table">
      <table className="w-full text-sm">
        <thead className="bg-zinc-50 border-b border-zinc-200">
          <tr className="text-left text-xs uppercase tracking-widest text-zinc-500">
            <th className="px-5 py-3">Member</th>
            <th className="px-5 py-3">Tier</th>
            <th className="px-5 py-3">Price</th>
            <th className="px-5 py-3">Renew</th>
            <th className="px-5 py-3">Status</th>
            <th className="px-5 py-3 text-right">Actions</th>
          </tr>
        </thead>
        <tbody>
          {members.map((m) => (
            <tr key={m.id} data-testid={`member-row-${m.id}`} className="border-b border-zinc-100 last:border-b-0">
              <td className="px-5 py-4">
                <div className="font-medium text-zinc-900">{m.member_name}</div>
                <div className="text-xs text-zinc-500">{m.member_email}</div>
              </td>
              <td className="px-5 py-4">{m.tier_label}</td>
              <td className="px-5 py-4 font-mono">{fmt(m.price_pence)}<span className="text-xs text-zinc-500"> / {INTERVAL_LABEL[m.interval]?.toLowerCase()}</span></td>
              <td className="px-5 py-4">{m.auto_renew ? "Auto" : "One-time"}</td>
              <td className="px-5 py-4">{statusBadge(m.status)}</td>
              <td className="px-5 py-4 text-right">
                {(m.status === "active" || m.status === "past_due") && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => doCancel(m)}
                    disabled={cancelling === m.id}
                    className="rounded-full"
                    data-testid={`cancel-member-${m.id}`}
                  >
                    {cancelling === m.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Ban className="w-3.5 h-3.5 mr-1" />}
                    Cancel
                  </Button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function TierEditor({ initial, symbol, onClose, onSaved }) {
  const [label, setLabel] = useState(initial?.label || "");
  const [description, setDescription] = useState(initial?.description || "");
  const [pricePounds, setPricePounds] = useState(initial ? ((initial.price_pence || 0) / 100).toFixed(2) : "");
  const [interval, setInterval] = useState(initial?.interval || "monthly");
  const [hideFee, setHideFee] = useState(initial ? !!initial.hide_booking_fee : true);
  const [perksText, setPerksText] = useState((initial?.perks || []).join("\n"));
  const [status, setStatus] = useState(initial?.status || "active");
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    const pounds = parseFloat(pricePounds);
    if (!label.trim() || !pounds || pounds <= 0) {
      toast.error("Enter a label and a price greater than 0");
      return;
    }
    const body = {
      label: label.trim(),
      description: description.trim() || null,
      price_pence: Math.round(pounds * 100),
      interval,
      hide_booking_fee: !!hideFee,
      perks: perksText.split("\n").map((p) => p.trim()).filter(Boolean),
    };
    setSaving(true);
    try {
      if (initial?.id) {
        await http.patch(`/venues/me/memberships/tiers/${initial.id}`, { ...body, status });
        toast.success("Tier updated");
      } else {
        await http.post("/venues/me/memberships/tiers", body);
        toast.success("Tier created");
      }
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
      setSaving(false);
    }
  };

  const doDelete = async () => {
    if (!initial?.id) return;
    if (!confirm(`Delete tier "${initial.label}"? Active subscribers stay on it — the tier just becomes archived.`)) return;
    try {
      const { data } = await http.delete(`/venues/me/memberships/tiers/${initial.id}`);
      toast.success(data.archived ? "Tier archived (had active members)" : "Tier deleted");
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  return (
    <Dialog open onOpenChange={(v) => { if (!v && !saving) onClose(); }}>
      <DialogContent data-testid="tier-editor-dialog" className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{initial ? "Edit tier" : "New membership tier"}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label>Label</Label>
            <Input value={label} onChange={(e) => setLabel(e.target.value)} placeholder="e.g. Gold, Patron, Friend" className="mt-1" data-testid="tier-label" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Price</Label>
              <div className="relative mt-1">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500">{symbol}</span>
                <Input type="number" step="0.01" min="0.01" value={pricePounds} onChange={(e) => setPricePounds(e.target.value)} className="pl-7" data-testid="tier-price" />
              </div>
            </div>
            <div>
              <Label>Billing</Label>
              <Select value={interval} onValueChange={setInterval}>
                <SelectTrigger className="mt-1" data-testid="tier-interval"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="monthly">Monthly</SelectItem>
                  <SelectItem value="six_monthly">Every 6 months</SelectItem>
                  <SelectItem value="annual">Annual</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label>Description (optional)</Label>
            <Textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={2} placeholder="Short pitch for what this membership includes." className="mt-1" data-testid="tier-description" />
          </div>
          <div>
            <Label>Perks (one per line, optional)</Label>
            <Textarea value={perksText} onChange={(e) => setPerksText(e.target.value)} rows={4} placeholder={"Free entry to comedy nights\nPriority booking on ticketed shows\nBring-a-friend Thursdays"} className="mt-1 font-mono text-xs" data-testid="tier-perks" />
          </div>
          <label className="flex items-start gap-2 cursor-pointer">
            <input type="checkbox" checked={hideFee} onChange={(e) => setHideFee(e.target.checked)} className="mt-1 rounded" data-testid="tier-hide-fee" />
            <div className="text-sm">
              <div className="font-medium">Hide booking fee in price shown to buyers</div>
              <div className="text-xs text-zinc-500 mt-0.5">Recommended. Ticket buyers see a single clean price with the booking fee baked in.</div>
            </div>
          </label>
          {initial && (
            <div>
              <Label>Status</Label>
              <Select value={status} onValueChange={setStatus}>
                <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">Active (visible to buyers)</SelectItem>
                  <SelectItem value="archived">Archived (hidden)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}
        </div>
        <DialogFooter className="gap-2">
          {initial && (
            <Button variant="ghost" onClick={doDelete} className="text-red-600 hover:text-red-700 mr-auto" data-testid="tier-delete">
              <Trash2 className="w-4 h-4 mr-1" /> Delete
            </Button>
          )}
          <Button variant="outline" onClick={onClose} disabled={saving} className="rounded-full">Cancel</Button>
          <Button onClick={submit} disabled={saving} className="rounded-full bg-zinc-900 hover:bg-zinc-800" data-testid="tier-save">
            {saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Saving…</> : (initial ? "Save changes" : "Create tier")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
